import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { type FunctionComponent, type JSX, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";

import { Form, FormControl, FormItem, FormMessage } from "@/components/ui/form";
import { FormField } from "@/components/ui/form/FormField";
import { Input } from "@/components/ui/input";
import {
  AUTH_INPUT,
  AUTH_LABEL,
  AUTH_LINK,
  AUTH_PRIMARY,
  AuthNotice,
  AuthShell,
} from "@/components/ui/native-auth/AuthShell";
import { underV3 } from "@/components/ui/v3/flagged";
import { cn } from "@/lib/utils";
import { ForgotPasswordSchema } from "@/schemas/forgotPassword.schema";
import { useAuthStore } from "@/store/auth.store";

type ForgotPasswordForm = z.infer<typeof ForgotPasswordSchema>;

export const ForgotPassword: FunctionComponent = () =>
  AppConfig.enableAppRedesign ? (
    <ForgotPasswordV3 />
  ) : (
    <ClassicForgotPassword />
  );

// v3.2 (VITE_APP_REDESIGN_ENABLED): the sign-in screens' shell, field and
// buttons (AuthShell), same request and messages.
export const ForgotPasswordV3 = (): JSX.Element => {
  const { t } = useTranslation();
  const { forgotPassword, loading } = useAuthStore.getState();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordSchema),
    shouldFocusError: true,
    defaultValues: { email: "" },
  });
  const {
    register,
    formState: { isValid, errors, isSubmitting },
  } = form;

  const onSubmit: SubmitHandler<ForgotPasswordForm> = async (values) => {
    setSentTo(null);
    setErrorMessage(null);
    try {
      await forgotPassword(values.email);
      setSentTo(values.email);
    } catch {
      setErrorMessage(t("forgotPassword.messages.error"));
    }
  };
  const busy = loading || isSubmitting;

  return (
    <AuthShell
      description={sentTo ? undefined : t("forgotPassword.v3.description")}
      footer={
        <Link className={AUTH_LINK} data-testid="forgot-password-back" to="/">
          {t("forgotPassword.backToSignIn")}
        </Link>
      }
      title={t("forgotPassword.v3.title")}
    >
      {sentTo ? (
        <AuthNotice tone="success">
          {t("forgotPassword.messages.success", { email: sentTo })}
        </AuthNotice>
      ) : (
        <form
          noValidate
          className="space-y-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div>
            <label className={AUTH_LABEL} htmlFor="forgot-password-email">
              {t("forgotPassword.v3.emailLabel")}
            </label>
            <input
              aria-invalid={errors.email ? true : undefined}
              autoComplete="email"
              className={AUTH_INPUT}
              id="forgot-password-email"
              inputMode="email"
              placeholder="you@clinic.com"
              type="email"
              {...register("email")}
            />
            {errors.email?.message && (
              <p className="mt-2 text-[13px] text-v3-rec-text" role="alert">
                {errors.email.message}
              </p>
            )}
          </div>
          {errorMessage && <AuthNotice tone="error">{errorMessage}</AuthNotice>}
          <button
            className={AUTH_PRIMARY}
            data-testid="forgot-password-submit-button"
            disabled={!isValid || busy}
            type="submit"
          >
            <Mail aria-hidden className="size-4" />
            {busy
              ? t("forgotPassword.form.loadingButton")
              : t("forgotPassword.form.resetButton")}
          </button>
        </form>
      )}
    </AuthShell>
  );
};

const ClassicForgotPassword: FunctionComponent = () => {
  const { t } = useTranslation();
  const { forgotPassword, loading } = useAuthStore.getState();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordSchema),
    shouldFocusError: true,
    defaultValues: {
      email: "",
    },
  });

  const {
    formState: { isValid },
  } = form;

  const onSubmit: SubmitHandler<ForgotPasswordForm> = async (values) => {
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      await forgotPassword(values.email);
      setSuccessMessage(
        t("forgotPassword.messages.success", { email: values.email }),
      );
    } catch (error_) {
      setErrorMessage(t("forgotPassword.messages.error"));
    }
  };

  return (
    <div
      className={cn(
        "w-full flex flex-col justify-center items-center",
        // v3.2 app: the sign-in card's look (classic tokens resolve to v3.2).
        underV3("v3-remap min-h-dvh bg-v3-canvas px-4 py-10 text-v3-text"),
      )}
    >
      <div
        className={cn(
          "w-full flex flex-col max-w-[450px] px-4 gap-4",
          underV3(
            "rounded-[18px] bg-v3-panel px-6 py-8 shadow-v3-hairline sm:px-8",
          ),
        )}
      >
        <h1
          className={cn(
            "text-[2rem] font-bold text-primary-dark dark:text-foreground text-center",
            underV3(
              "font-v3-display text-[26px] tracking-[-0.02em] text-v3-text",
            ),
          )}
        >
          {t("forgotPassword.title")}
        </h1>
        {successMessage ? (
          <div className="w-full text-center">
            <div className="mb-4 text-success">{successMessage}</div>
            <Link className="font-medium text-primary hover:underline" to="/">
              {t("forgotPassword.backTo")} {t("forgotPassword.loginLink")}
            </Link>
          </div>
        ) : (
          <>
            <div className="w-full mb-8">
              <p className="text-tinted-grey-4 text-xs font-normal">
                {t("forgotPassword.description")}
              </p>
            </div>
            <Form {...form}>
              <form
                noValidate
                className="w-full space-y-4"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="w-full flex flex-col space-y-1">
                      <FormControl>
                        <Input
                          icon={<Mail />}
                          placeholder={t("forgotPassword.form.emailLabel")}
                          {...field}
                        />
                      </FormControl>
                      {errorMessage && (
                        <div className="mt-1 text-xs text-error text-center">
                          {errorMessage}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  className="w-full"
                  data-testid="forgot-password-submit-button"
                  disabled={!isValid || loading}
                  size="xl"
                  type="submit"
                >
                  {loading
                    ? t("forgotPassword.form.loadingButton")
                    : t("forgotPassword.form.resetButton")}
                </Button>
              </form>
            </Form>
          </>
        )}

        {!successMessage && (
          <p className="mt-4 text-sm text-tinted-grey-3 text-center">
            {t("forgotPassword.backToLogin")}{" "}
            <Link className="font-medium text-primary hover:underline" to="/">
              {t("forgotPassword.backTo")} {t("forgotPassword.loginLink")}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};
